'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ApiSchema } from '@type/api/apiUtilityFn';

type ScreeningItemDto = ApiSchema<'ScreeningItemDto'>;
type ScreeningStatsDto = ApiSchema<'ScreeningStatsDto'>;

/** SSE event payloads */
interface SSEItemUpdateEvent {
  itemId: string;
  status: string;
  score?: number | null;
  matchLevel?: string | null;
}

interface SSEItemsUpdateEvent {
  items: ScreeningItemDto[];
  stats?: ScreeningStatsDto;
}

interface SSEProcessingCompleteEvent {
  allDone: boolean;
}

interface UseSSEScreeningItemsOptions {
  jobSlug: string | null;
  enabled?: boolean;
  /** Called when a full items snapshot is received */
  onItemsUpdate?: (items: ScreeningItemDto[], stats?: ScreeningStatsDto) => void;
  /** Called when a single item is updated */
  onItemUpdate?: (update: SSEItemUpdateEvent) => void;
  /** Called when all processing is complete */
  onProcessingComplete?: () => void;
}

/**
 * Hook to receive real-time screening item updates via Server-Sent Events (SSE).
 * Connects to /api/proxy/v1/screening/screenings/{jobSlug}/items/events and
 * updates state on each received event. Closes the connection on processing_complete or error.
 */
const MAX_RETRIES = 5;

export const useSSEScreeningItems = ({
  jobSlug,
  enabled = true,
  onItemsUpdate,
  onItemUpdate,
  onProcessingComplete,
}: UseSSEScreeningItemsOptions) => {
  const [items, setItems] = useState<ScreeningItemDto[]>([]);
  const [stats, setStats] = useState<ScreeningStatsDto | undefined>(undefined);
  const [isConnected, setIsConnected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const eventSourceRef = useRef<any>(null);
  const retryAttemptsRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const close = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    setIsProcessing(false);
  }, []);

  useEffect(() => {
    if (!enabled || !jobSlug) {
      return undefined;
    }

    // Reset retry attempts on successful new connection attempt
    retryAttemptsRef.current = 0;

    const url = `/api/proxy/v1/screening/screenings/${jobSlug}/items/events`;
    const es = new EventSource(url);
    eventSourceRef.current = es;
    setIsConnected(true);
    setIsProcessing(true);
    setError(null);

    /** items_update — full snapshot */
    es.addEventListener('items_update', (event: MessageEvent) => {
      try {
        const payload: SSEItemsUpdateEvent = JSON.parse(event.data as string);
        setItems(payload.items ?? []);
        if (payload.stats) {
          setStats(payload.stats);
        }
        onItemsUpdate?.(payload.items ?? [], payload.stats);

        // If we receive an empty items array, close the connection
        // Backend keeps the connection open for 10 minutes for empty projects,
        // but we don't need it if there are no items to process
        if (!payload.items || payload.items.length === 0) {
          setIsProcessing(false);
          close();
        }
      } catch {
        // ignore malformed events
      }
    });

    /** item_update — single item changed */
    es.addEventListener('item_update', (event: MessageEvent) => {
      try {
        const update: SSEItemUpdateEvent = JSON.parse(event.data as string);
        setItems((prev) => prev.map((item) => (item.id === update.itemId
          ? { ...item, status: update.status as ScreeningItemDto['status'] }
          : item)) as ScreeningItemDto[]);
        onItemUpdate?.(update);
      } catch {
        // ignore malformed events
      }
    });

    /** processing_complete — all done, close connection */
    es.addEventListener('processing_complete', (event: MessageEvent) => {
      try {
        const payload: SSEProcessingCompleteEvent = JSON.parse(event.data as string);
        if (payload.allDone) {
          setIsProcessing(false);
          onProcessingComplete?.();
          close();
        }
      } catch {
        // ignore malformed events
      }
    });

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      setIsConnected(false);

      if (retryAttemptsRef.current < MAX_RETRIES) {
        const delay = Math.min(1000 * 2 ** retryAttemptsRef.current, 30000);
        retryAttemptsRef.current += 1;
        retryTimerRef.current = setTimeout(() => {
          setRetryCount((c) => c + 1);
        }, delay);
      } else {
        setError(new Error('SSE connection failed after multiple retries'));
        setIsProcessing(false);
      }
    };

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      close();
    };
  }, [close, enabled, jobSlug, onItemUpdate, onItemsUpdate, onProcessingComplete, retryCount]);

  // Derive processing state from items when we have them
  const isItemsProcessing = items.some(
    (item) => ['PENDING_PARSING', 'PARSING', 'PENDING_MATCHING', 'MATCHING'].includes(item.status ?? ''),
  );

  const completedCount = items.filter((item) => ['MATCHED', 'MATCH_FAILED', 'INTERVIEW_SCHEDULED', 'INTERVIEW_IN_PROGRESS', 'INTERVIEW_COMPLETED', 'INTERVIEW_FAILED'].includes(item.status ?? '')).length;
  const errorCount = items.filter((item) => ['PARSE_FAILED', 'MATCH_FAILED', 'INTERVIEW_FAILED'].includes(item.status ?? '')).length;

  return {
    items,
    stats,
    isConnected,
    isProcessing: isProcessing || isItemsProcessing,
    completedCount,
    errorCount,
    totalCount: items.length,
    error,
    close,
  };
};

export default useSSEScreeningItems;
